<template>
    <template v-if="leadCollection">
        <flex-container>
            <flex-container>
                <flex-container :size="5">
                    <h1>
                        Contacten
                    </h1>
                </flex-container>
            </flex-container>

            <card-normal>
                <flex-container direction="column">
                    <text-heading>
                        Weergegeven flowtype
                    </text-heading>

                    <lead-list-pipeline-category-filters
                        :pipeline-collection="pipelineCollection"
                        :active-pipeline-category="selectedPipelineCategory as PipelineCategory"
                        @filter-pipeline-category="setSelectedPipelineCategory($event as PipelineCategory)"
                    />
                </flex-container>
            </card-normal>

            <div>
                <lead-list-status-filters
                    v-if="!selectedPipelineCategory"
                    :active-status="selectedStatus as Status"
                    @filter-status="setSelectedStatus($event as Status)"
                />
                <lead-list-phase-category-status-filters
                    v-else
                    :pipeline-collection="pipelineCollection"
                    :active-status="selectedStatus as Status"
                    :active-phase-category="selectedPhaseCategory as PhaseCategory"
                    :active-pipeline-id="selectedPipelineId as PipelineId"
                    :active-pipeline-category="selectedPipelineCategory as PipelineCategory"
                    @filter-status="setSelectedStatus($event as Status)"
                    @filter-phase-category="setSelectedPhaseCategory($event as PhaseCategory)"
                />

                <card-normal
                    :with-overflow="false"
                    class="lead-list-view__list-card"
                    without-padding
                >
                    <lead-list
                        :lead-collection="leadCollection"
                        :selected-sort="selectedSort"
                        :selected-item-per-page="selectedItemsPerPage"
                        @sort="selectedSort = $event; load()"
                    >
                        <template #flowFilter>
                            <lead-list-pipeline-filters
                                :pipeline-collection="pipelineCollection"
                                :active-pipeline-id="selectedPipelineId as PipelineId"
                                :active-pipeline-category="selectedPipelineCategory as PipelineCategory"
                                @filter-pipeline-id="setSelectedPipelineId($event as PipelineId)"
                            />
                        </template>
                    </lead-list>
                </card-normal>

                <lead-list-pagination
                    :page="selectedPage ?? 1"
                    :total-items="leadCollection.total.value"
                    @filter="selectedPage = $event; load()"
                    @items-per-page-change="setItemsPerPage($event)"
                />
            </div>
        </flex-container>
    </template>

    <template v-else>
        <lead-collection-empty-message />
    </template>

    <router-view />
</template>

<script setup lang="ts">
    import { computed, onMounted, provide, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { Lead, PhaseCategory, PipelineCategory, PipelineId, Pipeline, Status, CountLeadsCommand } from '@keyvue/one-api-client';
    import LeadCollectionEmptyMessage from '@/lead/lead/components/LeadCollectionEmptyMessage.vue';
    import LeadList from '@/lead/lead/components/LeadList.vue';
    import LeadListStatusFilters from '@/lead/lead/components/LeadListStatusFilters.vue';
    import { type RouteLocationRaw, useRouter } from 'vue-router';
    import LeadListPhaseCategoryStatusFilters from '@/lead/lead/components/LeadListPhaseCategoryStatusFilters.vue';
    import LeadListPagination from '@/lead/lead/components/LeadListPagination.vue';
    import LeadListPipelineCategoryFilters from '@/lead/lead/components/LeadListPipelineCategoryFilters.vue';
    import LeadListPipelineFilters from '@/lead/lead/components/LeadListPipelineFilters.vue';

    const { getLazyResourceCollection, currentCompany, client } = useClient();
    const router = useRouter();

    const pipelineCollection = getLazyResourceCollection(Pipeline, { company: currentCompany.value?.id });
    pipelineCollection.load();

    const selectedPipelineId = ref<PipelineId | null>(null);
    const selectedPipelineCategory = ref<PipelineCategory | null>(null);
    const selectedPhaseCategory = ref<PhaseCategory | null>(null);
    const selectedStatus = ref<Status | null>(null);
    const selectedPage = ref<number | null>(null);
    const selectedItemsPerPage = ref<number>(30);
    const selectedSort = ref<{ column: string, direction: 'ASC' | 'DESC' } | null>(null);

    const leadCollection = getLazyResourceCollection(Lead);
    const counts = ref({});

    const filters = computed<{ [key: string]: any }>(() => {
        const filterObject: Record<string, any> = {};

        if (selectedPipelineCategory.value) {
            filterObject['pipeline.category'] = selectedPipelineCategory.value;

            if (selectedPhaseCategory.value) {
                filterObject['phase.category'] = selectedPhaseCategory.value;
            }
        } else {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            selectedPhaseCategory.value = null;
        }

        if (selectedPipelineId.value) {
            filterObject.pipeline = selectedPipelineId.value;
        }

        if (selectedStatus.value) {
            filterObject.status = selectedStatus.value;
        }

        if (selectedPage.value) {
            filterObject.page = selectedPage.value;
        }

        if (selectedItemsPerPage.value) {
            filterObject.itemsPerPage = selectedItemsPerPage.value;
        }

        if (selectedSort.value) {
            filterObject[`order[${selectedSort.value.column}]`] = selectedSort.value.direction;
        }

        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        router.push({
            name: router.currentRoute.value.name,
            query: filterObject,
        } as RouteLocationRaw);

        filterObject.pagination = true;

        return filterObject;
    });

    async function setSelectedPipelineCategory(category: PipelineCategory | null) {
        selectedPipelineCategory.value = category;
        selectedPipelineId.value = null;
        selectedPhaseCategory.value = null;
        selectedStatus.value = Status.OPEN;
        await load(true);
    }

    async function setSelectedStatus(status: Status) {
        selectedStatus.value = status;
        selectedPhaseCategory.value = null;
        await load(true);
    }

    async function setSelectedPhaseCategory(category: PhaseCategory | null) {
        selectedPhaseCategory.value = category;
        selectedStatus.value = Status.OPEN;
        await load(true);
    }

    async function setSelectedPipelineId(pipelineId: PipelineId) {
        selectedPipelineId.value = pipelineId;
        await load();
    }

    async function setItemsPerPage(itemsPerPage: number) {
        selectedItemsPerPage.value = itemsPerPage;
        await load(true);
    }

    function loadQuery() {
        const queryParams = router.currentRoute.value.query;

        selectedStatus.value = Status.OPEN;

        if (queryParams['pipeline.category']) {
            selectedPipelineCategory.value = new PipelineCategory(queryParams['pipeline.category'].toString());

            if (queryParams['phase.category']) {
                selectedPhaseCategory.value = new PhaseCategory(queryParams['phase.category'].toString());
            }
        }

        if (queryParams.pipeline) {
            selectedPipelineId.value = new PipelineId(queryParams.pipeline.toString());
        }

        if (queryParams.status && selectedPhaseCategory.value === null) {
            selectedStatus.value = new Status(queryParams.status.toString());
        }

        if (queryParams.page) {
            selectedPage.value = Number.parseInt(queryParams.page.toString(), 10);
        }

        if (queryParams.itemsPerPage) {
            selectedItemsPerPage.value = Number.parseInt(queryParams.itemsPerPage.toString(), 10);
        }

        const orderKey = Object.keys(queryParams).find((key) => (/order\[/g.test(key)));

        if (orderKey) {
            const direction = queryParams[orderKey] as any;
            const trimmedColumnName = orderKey.split('[')[1];
            const column = trimmedColumnName.substring(0, trimmedColumnName.length - 1);
            selectedSort.value = {
                column,
                direction,
            };
        }
    }

    async function load(resetSelectedPage: boolean = false) {
        if (resetSelectedPage) {
            selectedPage.value = null;
        }

        await leadCollection.load(filters.value);

        counts.value = await client.dispatch(new CountLeadsCommand(
            selectedPipelineCategory.value as PipelineCategory,
        ));
    }

    onMounted(async () => {
        loadQuery();
        await load();
    });

    provide('counts', counts);
</script>

<style lang="scss" scoped>
.card-normal.lead-list-view__list-card {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}
</style>
